import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity'
import Popup from '../Common/Popup';
import useModule from '../../hooks/useModule';

/**
 * The `RegionFilter` function is a React component that renders a filter for selecting regions, with
 * options that can be expanded or collapsed.
 * @returns a JSX element.
 */
function RegionFilter({ RegionOptions, setRegionOptions, customPathname, onChange, filterPathkey = "region", label = "Region", isPopupOpen, setIsPopupOpen }) {
  const { refineSearchFilters, accessControl } = useContext(PeFilterContext);
  const [internalPopupOpen, setInternalPopupOpen] = useState(false);
  const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
  const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
  const { isPE, isVC, isMA } = useModule();

  useEffect(() => {
    const ids = refineSearchFilters[customPathname]?.[filterPathkey]?.map(value => value.id) ?? [];
    setRegionOptions(RegionOptions?.map(option => ({ ...option, isChecked: ids.includes(option.id) })))
  }, [customPathname, refineSearchFilters, filterPathkey, setRegionOptions])

  const handleRegionChange = (e) => {
    const { name, checked, id, selectedEntities, filteredIds } = e.target;
    let updatedOptions;

    if (name === "allSelect") {
        updatedOptions = RegionOptions.map(region => ({ ...region, isChecked: checked }));
    } else if (name === "filteredSelect" && filteredIds) {
        updatedOptions = RegionOptions.map(region => ({
            ...region,
            isChecked: filteredIds.includes(region.id) ? checked : region.isChecked
        }));
    } else if (name === "selectedEntities" && selectedEntities) {
        const selectedEntityIds = selectedEntities.map(entity => parseInt(entity.id));
        updatedOptions = RegionOptions.map(region => ({
            ...region,
            isChecked: selectedEntityIds.includes(region.id) ? !region.isChecked : region.isChecked
        }));
    } else {
        updatedOptions = RegionOptions.map(region =>
            region.id === parseInt(id) ? { ...region, isChecked: checked } : region
        );
    }

    setRegionOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const selectedRegionCount = RegionOptions?.filter(option => option.isChecked)?.length ?? 0

  const enabledIds = accessControl ? (isPE || isVC ? accessControl?.pe_regions?.id : isMA && label !== "Acquirer Region" ? accessControl?.ma_regions?.id : []) : [];

  const disabledRegions = enabledIds?.length > 0 ? RegionOptions?.filter(op => !enabledIds?.includes(op.id)) : [];

  return (
    <div className="relative w-full md:inline-block">
      <button
        type="button"
        className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
        onClick={() => setPopupOpen(!popupOpen)}
      >
        <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {RegionOptions.slice(0,2).map(item => item.name).join(', ')}</span></span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {selectedRegionCount > 0 && (
            <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
              {selectedRegionCount}
            </span>
          )}
          <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
            ›
          </span>
        </div>
      </button>

      {popupOpen && (
        <Popup onClose={() => setPopupOpen(false)} label={label}>
          <div className="p-4">
            <MultiSelectEntity
              handleEntityChange={handleRegionChange}
              primaryEntity={RegionOptions}
              disabled={disabledRegions}
              searchBox={false}
            />
          </div>
        </Popup>
      )}
    </div>
  )
}

export default RegionFilter